import React, {useState, useCallback, createContext, useContext, ReactNode, useEffect, useRef} from "react";
import { createPortal } from "react-dom";
import { ModalDrawer } from "ui";

interface ModalProps {
    close: () => void;
    zIndex: number;
}

interface ModalContextType {
    show: (content: React.FC<ModalProps>, index?: number) => void;
    open: (props: {
        title: ReactNode,
        content: React.ComponentType<{ close: () => void, zIndex: number }>,
        index?: number
    }) => void;
}

interface ModalState {
    modals: Array<{
        visible: boolean;
        index: number;
        zIndex: number;
        content: React.FC<ModalProps>;
        close: () => void;
    }>;
    highestZIndex: number;
}

const ModalContext = createContext<ModalContextType | null>(null);

export function ModalProvider({ children }: { children: ReactNode }) {
    const [modalState, setModalState] = useState<ModalState>({
        modals: [],
        highestZIndex: 100,
    });
    const highestIndexRef = useRef(100); // 记录当前的最高 index

    const getNextIndex = useCallback(() => {
        highestIndexRef.current += 1;
        return highestIndexRef.current;
    }, []);


    const closeModal = useCallback((index: number) => {
        // console.log("closeModal", index);
        setModalState(prevState => ({
            modals: prevState.modals.filter(modal => modal.index !== index),
            highestZIndex: prevState.highestZIndex,
        }));
    }, []);

    // useEffect(() => {
    //     console.log("modalState", modalState);
    // }, [modalState]);

    const show = useCallback((content: React.FC<ModalProps>, index?: number) => {
        setModalState(prevState => {
            const modalIndex = index ?? getNextIndex();
            const newZIndex = prevState.highestZIndex + 1;
            return {
                modals: [...prevState.modals, {
                    visible: true,
                    index: modalIndex,
                    zIndex: newZIndex,
                    content: content,
                    close: () => closeModal(modalIndex),
                }],
                highestZIndex: newZIndex,
            };
        });
    }, [getNextIndex, closeModal]);

    const open = useCallback((props: {
        title: ReactNode;
        content: React.ComponentType<{ close: () => void, zIndex: number }>;
        index?: number;
        onBack?: () => void;
    }) => {
        setModalState(prevState => {
            const modalIndex = props.index ?? getNextIndex();
            const newZIndex = prevState.highestZIndex + 1;
            return {
                modals: [...prevState.modals, {
                    visible: true,
                    index: modalIndex,
                    zIndex: newZIndex,
                    close: () => closeModal(modalIndex),
                    content: ({ close, zIndex }) => <ModalDrawer showClose isOpen onClose={close} title={props.title} zIndex={zIndex} onBack={props.onBack}>
                        <props.content close={close} zIndex={zIndex} />
                    </ModalDrawer>,
                }],
                highestZIndex: newZIndex,
            };
        });
    }, [getNextIndex, closeModal]);

    const modalElements = modalState.modals.map(modal =>
        modal.visible ? (
            createPortal(
                <modal.content key={modal.index} zIndex={modal.zIndex} close={modal.close} />,
                document.body
            )
        ) : null
    )


    return (
        <ModalContext.Provider value={{ show, open }}>
            {children}
            {modalElements}
        </ModalContext.Provider>
    );
}

export function useModal(): ModalContextType {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error("useModal must be used within a ModalProvider");
    }
    return context;
}
